import { IDeviceEvent, IDeviceTimelineItem } from '../../api/types';

export function getDeviceEventKey(event: IDeviceEvent): string {
  if (event.id) return `id:${event.id}`;

  return [
    event.timestamp,
    event.serialNumber ?? '',
    event.description,
  ].join('|');
}

export function mergeDeviceEvents(
  existing: IDeviceEvent[],
  incoming: IDeviceEvent[],
): IDeviceEvent[] {
  const events = new Map<string, IDeviceEvent>();

  for (const event of [...existing, ...incoming]) {
    events.set(getDeviceEventKey(event), event);
  }

  return [...events.values()].sort(
    (left, right) =>
      new Date(right.timestamp).getTime() -
      new Date(left.timestamp).getTime(),
  );
}

export function hasNewDeviceEvents(
  existing: IDeviceEvent[],
  incoming: IDeviceEvent[],
): boolean {
  const existingKeys = new Set(existing.map(getDeviceEventKey));
  return incoming.some(
    (event) => !existingKeys.has(getDeviceEventKey(event)),
  );
}

export function getDeviceTimelineItemKey(
  item: IDeviceTimelineItem,
): string {
  if (item.type === 'command') {
    return ['command', item.commandId ?? '', item.timestamp].join(
      '|',
    );
  }

  return [
    'event',
    item.eventId ?? '',
    item.timestamp,
    item.description,
  ].join('|');
}

export function getOldestPendingCommandTimestamp(
  items: IDeviceTimelineItem[],
): string | undefined {
  return items
    .filter(
      (item) =>
        item.type === 'command' &&
        (item.deliveredAt == null || item.wasAccepted == null),
    )
    .map((item) => item.timestamp)
    .filter(
      (timestamp) => !Number.isNaN(new Date(timestamp).getTime()),
    )
    .sort(
      (left, right) =>
        new Date(left).getTime() - new Date(right).getTime(),
    )[0];
}

export function mergeDeviceTimeline(
  existing: IDeviceTimelineItem[],
  incoming: IDeviceTimelineItem[],
): IDeviceTimelineItem[] {
  const items = new Map<string, IDeviceTimelineItem>();

  for (const item of [...existing, ...incoming]) {
    items.set(getDeviceTimelineItemKey(item), item);
  }

  return [...items.values()].sort(
    (left, right) =>
      new Date(right.timestamp).getTime() -
      new Date(left.timestamp).getTime(),
  );
}

export function hasNewDeviceTimelineItems(
  existing: IDeviceTimelineItem[],
  incoming: IDeviceTimelineItem[],
): boolean {
  const existingKeys = new Set(
    existing.map(getDeviceTimelineItemKey),
  );
  return incoming.some(
    (item) => !existingKeys.has(getDeviceTimelineItemKey(item)),
  );
}
